import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { VideoCard } from '../components/VideoCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ListMusic, Plus, MoreHorizontal, Trash2, Play } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1764258906659-e966a089ed7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
];

export const PlaylistsPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { axiosInstance } = useAuth();
  const { playVideo } = usePlayer();
  const location = useLocation();

  const fetchPlaylists = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/playlists');
      setPlaylists(res.data);
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const fetchPlaylistVideos = useCallback(async (playlistId) => {
    try {
      const res = await axiosInstance.get(`/playlists/${playlistId}/videos`);
      setPlaylistVideos(res.data);
    } catch (err) {
      console.error('Failed to fetch playlist videos:', err);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  // Honor a playlist preselect passed via navigation state from the Library page.
  useEffect(() => {
    const preselectId = location.state?.playlistId;
    if (preselectId && playlists.length > 0) {
      const found = playlists.find((p) => p.id === preselectId);
      if (found) setSelectedPlaylist(found);
    }
  }, [location.state, playlists]);

  useEffect(() => {
    if (selectedPlaylist) {
      fetchPlaylistVideos(selectedPlaylist.id);
    }
  }, [selectedPlaylist, fetchPlaylistVideos]);

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    
    try {
      await axiosInstance.post('/playlists', { name: newPlaylistName });
      toast.success('Playlist created!');
      setNewPlaylistName('');
      setShowCreateDialog(false);
      fetchPlaylists();
    } catch (err) {
      toast.error('Failed to create playlist');
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    try {
      await axiosInstance.delete(`/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
        setPlaylistVideos([]);
      }
      fetchPlaylists();
    } catch (err) {
      toast.error('Failed to delete playlist');
    }
  };

  const handleRemoveFromPlaylist = async (videoId) => {
    if (!selectedPlaylist) return;
    
    try {
      await axiosInstance.delete(`/playlists/${selectedPlaylist.id}/videos/${videoId}`);
      toast.success('Removed from playlist');
      fetchPlaylistVideos(selectedPlaylist.id);
      fetchPlaylists();
    } catch (err) {
      toast.error('Failed to remove video');
    }
  };

  const getThumbnail = (video) => {
    return resolveThumbnailUrl(video);
  };

  const getPlaylistThumbnail = (playlist, index) => {
    if (playlist.thumbnail) {
      return `${API}/api/files/${playlist.thumbnail}`;
    }
    return MOCK_THUMBNAILS[index % MOCK_THUMBNAILS.length];
  };

  if (selectedPlaylist) {
    return (
      <div className="pb-32 px-6">
        <div className="py-8">
          <Button
            variant="ghost"
            onClick={() => setSelectedPlaylist(null)}
            className="text-zinc-400 hover:text-white mb-4"
            data-testid="back-to-playlists"
          >
            ← Back to Playlists
          </Button>

          <div className="flex items-center gap-4 mb-8">
            <div 
              className="w-24 h-24 rounded-xl bg-cover bg-center bg-zinc-800"
              style={{ backgroundImage: `url(${getPlaylistThumbnail(selectedPlaylist, 0)})` }}
            />
            <div>
              <h1 className="text-3xl font-bold text-white">
                {selectedPlaylist.name}
              </h1>
              <p className="text-zinc-400">{selectedPlaylist.video_count} videos</p>
            </div>
            {playlistVideos.length > 0 && (
              <Button
                onClick={() => playVideo(playlistVideos[0], playlistVideos, 0)}
                className="ml-auto bg-white hover:bg-zinc-200 text-black rounded-full px-6"
                data-testid="play-playlist-btn"
              >
                <Play className="w-5 h-5 mr-2 fill-white" />
                Play All
              </Button>
            )}
          </div>

          {playlistVideos.length === 0 ? (
            <div className="text-center py-20">
              <ListMusic className="w-20 h-20 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl text-zinc-400 mb-2">This playlist is empty</h3>
              <p className="text-zinc-500">Add videos from the browse page</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {playlistVideos.map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  thumbnail={getThumbnail(video, index)}
                  onClick={() => playVideo(video, playlistVideos, index)}
                  showRemove
                  onRemove={() => handleRemoveFromPlaylist(video.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-32 px-6">
      <div className="py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-zinc-800/60">
              <ListMusic className="w-6 h-6 text-zinc-300" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              My Playlists
            </h1>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-zinc-900 border border-zinc-700 text-white hover:bg-zinc-800 rounded-full"
                data-testid="create-playlist-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Playlist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Playlist</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Playlist name"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="playlist-name-input"
                />
                <Button
                  onClick={handleCreatePlaylist}
                  className="w-full bg-white text-black rounded-full"
                  data-testid="confirm-create-playlist-btn"
                >
                  Create
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-square bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : playlists.length === 0 ? (
          <div className="text-center py-20">
            <ListMusic className="w-20 h-20 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-zinc-400 mb-2">No playlists yet</h3>
            <p className="text-zinc-500">Create your first playlist to organize your videos</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {playlists.map((playlist, index) => (
              <div
                key={playlist.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden card-hover cursor-pointer group"
                onClick={() => setSelectedPlaylist(playlist)}
                data-testid={`playlist-card-${playlist.id}`}
              >
                <div 
                  className="aspect-square bg-cover bg-center bg-zinc-800 relative"
                  style={{ backgroundImage: `url(${getPlaylistThumbnail(playlist, index)})` }}
                >
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                      <Play className="w-7 h-7 text-white fill-white" />
                    </div>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold truncate">{playlist.name}</h3>
                    <p className="text-zinc-500 text-sm">{playlist.video_count} videos</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
                        <MoreHorizontal className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                      <DropdownMenuItem
                        className="text-red-400 hover:text-red-300 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlaylist(playlist.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Playlist
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
